"use client";

import { useEffect, useState, useCallback } from "react";
import {
  X,
  RotateCcw,
  Sun,
  Contrast,
  Type,
  Link2,
  Heading,
  Image as ImageIcon,
  MousePointer2,
  ZoomIn,
  Accessibility,
} from "lucide-react";

type ContrastMode = "none" | "uncolored" | "bright" | "reverse";
type CursorMode = "none" | "big-white" | "big-black";

interface A11ySettings {
  contrast: ContrastMode;
  fontScale: number;
  readableText: boolean;
  underlineLinks: boolean;
  underlineHeaders: boolean;
  imageTitles: boolean;
  cursor: CursorMode;
  zoomed: boolean;
}

const DEFAULT_SETTINGS: A11ySettings = {
  contrast: "none",
  fontScale: 100,
  readableText: false,
  underlineLinks: false,
  underlineHeaders: false,
  imageTitles: false,
  cursor: "none",
  zoomed: false,
};

const STORAGE_KEY = "lilylanka-a11y-settings";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<A11ySettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(saved) });
    } catch {
      // ignore corrupted storage
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const html = document.documentElement;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));

    html.classList.remove("a11y-uncolored", "a11y-bright", "a11y-reverse");
    if (settings.contrast === "uncolored") html.classList.add("a11y-uncolored");
    if (settings.contrast === "bright") html.classList.add("a11y-bright");
    if (settings.contrast === "reverse") html.classList.add("a11y-reverse");

    html.style.fontSize = `${settings.fontScale}%`;
    html.classList.toggle("a11y-readable", settings.readableText);
    html.classList.toggle("a11y-underline-links", settings.underlineLinks);
    html.classList.toggle("a11y-underline-headers", settings.underlineHeaders);

    html.classList.remove("a11y-cursor-white", "a11y-cursor-black");
    if (settings.cursor === "big-white")
      html.classList.add("a11y-cursor-white");
    if (settings.cursor === "big-black")
      html.classList.add("a11y-cursor-black");

    html.classList.toggle("a11y-zoomed", settings.zoomed);
  }, [settings, loaded]);

  useEffect(() => {
    if (!settings.imageTitles) return;
    document.querySelectorAll<HTMLImageElement>("img[alt]").forEach((img) => {
      if (!img.title) img.title = img.alt;
    });
  }, [settings.imageTitles]);

  const update = useCallback(
    <K extends keyof A11ySettings>(key: K, value: A11ySettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const toggleContrast = (mode: ContrastMode) =>
    update("contrast", settings.contrast === mode ? "none" : mode);
  const toggleCursor = (mode: CursorMode) =>
    update("cursor", settings.cursor === mode ? "none" : mode);
  const increaseText = () =>
    update("fontScale", Math.min(settings.fontScale + 10, 160));
  const decreaseText = () =>
    update("fontScale", Math.max(settings.fontScale - 10, 80));
  const reset = () => setSettings(DEFAULT_SETTINGS);

  return (
    <>
      <style jsx global>{`
        html.a11y-uncolored {
          filter: grayscale(1);
        }
        html.a11y-bright {
          filter: contrast(1.3) brightness(1.15);
        }
        html.a11y-reverse {
          filter: invert(1) hue-rotate(180deg);
        }
        html.a11y-reverse img,
        html.a11y-reverse video,
        html.a11y-reverse svg {
          filter: invert(1) hue-rotate(180deg);
        }
        html.a11y-readable body,
        html.a11y-readable body * {
          font-family: Arial, Helvetica, sans-serif !important;
          letter-spacing: 0.02em !important;
          line-height: 1.7 !important;
        }
        html.a11y-underline-links a {
          text-decoration: underline !important;
        }
        html.a11y-underline-headers h1,
        html.a11y-underline-headers h2,
        html.a11y-underline-headers h3,
        html.a11y-underline-headers h4,
        html.a11y-underline-headers h5,
        html.a11y-underline-headers h6 {
          text-decoration: underline !important;
        }
        html.a11y-cursor-white,
        html.a11y-cursor-white * {
          cursor:
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"><path fill="white" stroke="black" stroke-width="1" d="M4 2l16 6-6 2-2 6z"/></svg>')
              0 0,
            auto !important;
        }
        html.a11y-cursor-black,
        html.a11y-cursor-black * {
          cursor:
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"><path fill="black" stroke="white" stroke-width="1" d="M4 2l16 6-6 2-2 6z"/></svg>')
              0 0,
            auto !important;
        }
        html.a11y-zoomed body {
          transform: scale(1.25);
          transform-origin: top left;
          width: 80%;
        }

        .a11y-tool-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 4px;
          border-radius: 14px;
          border: 1.5px solid var(--border, #e5e7eb);
          background: var(--background, #fff);
          color: var(--ink-60, #6b7280);
          font-size: 0.66rem;
          font-weight: 700;
          font-family: inherit;
          text-align: center;
          cursor: pointer;
          min-height: 74px;
          box-shadow: none;
          transition: all 0.18s ease;
        }
        .a11y-tool-btn:hover {
          border-color: rgba(91, 40, 143, 0.3);
          background: rgba(91, 40, 143, 0.06);
          color: #5b288f;
        }
        .a11y-tool-btn--active {
          border-color: rgba(91, 40, 143, 0.45) !important;
          background: rgba(91, 40, 143, 0.12) !important;
          color: #5b288f !important;
          box-shadow: 0 4px 14px rgba(91, 40, 143, 0.2) !important;
          outline: 2px solid rgba(91, 40, 143, 0.35);
          outline-offset: -1px;
        }
        html.dark .a11y-tool-btn {
          background: var(--background);
          color: var(--foreground);
          border-color: rgba(255, 255, 255, 0.1);
        }
        html.dark .a11y-tool-btn:hover {
          background: rgba(124, 58, 191, 0.15);
          border-color: rgba(124, 58, 191, 0.4);
          color: #c084fc;
        }
        html.dark .a11y-tool-btn--active {
          background: rgba(124, 58, 191, 0.25) !important;
          border-color: rgba(124, 58, 191, 0.6) !important;
          color: #c084fc !important;
          outline-color: rgba(124, 58, 191, 0.5);
        }

        @keyframes a11y-pulse {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          70% {
            transform: scale(1.55);
            opacity: 0;
          }
          100% {
            transform: scale(1.55);
            opacity: 0;
          }
        }
        .a11y-pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #5b288f;
          animation: a11y-pulse 2.6s ease-out infinite;
          pointer-events: none;
        }

        html.dark .a11y-panel {
          background: var(--background);
          box-shadow:
            0 24px 64px rgba(0, 0, 0, 0.5),
            0 4px 18px rgba(0, 0, 0, 0.3);
        }
        html.dark .a11y-tool-btn {
          background: var(--background);
          color: var(--foreground);
          border-color: rgba(255, 255, 255, 0.1);
        }
        html.dark .a11y-section {
          border-bottom-color: rgba(255, 255, 255, 0.08);
        }

        @media (min-width: 640px) {
          .a11y-panel {
            top: 16px !important;
            left: 16px !important;
            width: 340px !important;
            height: auto !important;
            max-height: calc(100dvh - 32px) !important;
            border-radius: 24px !important;
          }
        }

        @keyframes a11y-panel-in {
          from {
            opacity: 0;
            transform: translateY(24px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @media (min-width: 640px) {
          @keyframes a11y-panel-in {
            from {
              opacity: 0;
              transform: translateX(-14px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
          }
        }
        html.dark .a11y-pulse-ring {
          background: #7c3abf;
        }
      `}</style>

      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Accessibility tools"
        style={{
          position: "fixed",
          bottom: 24,
          left: 24,
          zIndex: 9998,
          width: 48,
          height: 48,
          borderRadius: "50%",

          background: "#5b288f",
          color: "#fff",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 28px rgba(91,40,143,0.38)",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform =
            "translateY(-3px) scale(1.05)";
          (e.currentTarget as HTMLElement).style.boxShadow =
            "0 16px 38px rgba(91,40,143,0.5)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform =
            "translateY(0) scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow =
            "0 10px 28px rgba(91,40,143,0.38)";
        }}
      >
        <span className="a11y-pulse-ring" />
        <Accessibility size={28} style={{ position: "relative", zIndex: 1 }} />
      </button>

      {/* Panel */}
      {open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
            width: "100vw",
            height: "100dvh",
            background: "var(--background, #fff)",
            boxShadow:
              "0 24px 64px rgba(91,40,143,0.22), 0 4px 18px rgba(0,0,0,0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            animation: "a11y-panel-in 0.3s cubic-bezier(0.16,1,0.3,1) both",
          }}
          className="a11y-panel"
        >
          {/* Header */}
          <div
            style={{
              background: "#5b288f",
              padding: "20px 20px 18px",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.7)",
                  margin: 0,
                  marginBottom: 4,
                  fontFamily: "Display",
                }}
              >
                LilyLanka
              </p>
              <h2
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                  margin: 0,
                  fontFamily: "poppins",
                }}
              >
                Accessibility Tools
              </h2>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close accessibility panel"
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.28)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(255,255,255,0.3)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(255,255,255,0.18)";
              }}
            >
              <X size={15} />
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            <Section title="Color Contrast">
              <ToolButton
                icon={<Contrast size={19} />}
                label="Uncolored"
                active={settings.contrast === "uncolored"}
                onClick={() => toggleContrast("uncolored")}
              />
              <ToolButton
                icon={<Sun size={19} />}
                label="Bright"
                active={settings.contrast === "bright"}
                onClick={() => toggleContrast("bright")}
              />
              <ToolButton
                icon={
                  <Contrast size={19} style={{ transform: "rotate(180deg)" }} />
                }
                label="Reverse"
                active={settings.contrast === "reverse"}
                onClick={() => toggleContrast("reverse")}
              />
            </Section>

            <Section title="Text Size">
              <ToolButton
                icon={<span style={{ fontSize: 18, fontWeight: 800 }}>A+</span>}
                label="Increase"
                active={false}
                onClick={increaseText}
              />
              <ToolButton
                icon={<span style={{ fontSize: 15, fontWeight: 800 }}>A-</span>}
                label="Decrease"
                active={false}
                onClick={decreaseText}
              />
              <ToolButton
                icon={<Type size={19} />}
                label="Readable"
                active={settings.readableText}
                onClick={() => update("readableText", !settings.readableText)}
              />
            </Section>

            <Section title="Highlighting">
              <ToolButton
                icon={<Link2 size={19} />}
                label="Underline Links"
                active={settings.underlineLinks}
                onClick={() =>
                  update("underlineLinks", !settings.underlineLinks)
                }
              />
              <ToolButton
                icon={<Heading size={19} />}
                label="Underline Headers"
                active={settings.underlineHeaders}
                onClick={() =>
                  update("underlineHeaders", !settings.underlineHeaders)
                }
              />
              <ToolButton
                icon={<ImageIcon size={19} />}
                label="Image Titles"
                active={settings.imageTitles}
                onClick={() => update("imageTitles", !settings.imageTitles)}
              />
            </Section>

            <Section title="Cursor & Zoom" noBorder>
              <ToolButton
                icon={<MousePointer2 size={19} />}
                label="White Cursor"
                active={settings.cursor === "big-white"}
                onClick={() => toggleCursor("big-white")}
              />
              <ToolButton
                icon={<MousePointer2 size={19} fill="black" />}
                label="Black Cursor"
                active={settings.cursor === "big-black"}
                onClick={() => toggleCursor("big-black")}
              />
              <ToolButton
                icon={<ZoomIn size={19} />}
                label="Zoom Screen"
                active={settings.zoomed}
                onClick={() => update("zoomed", !settings.zoomed)}
              />
            </Section>
          </div>

          {/* Reset */}
          <div style={{ padding: 16, flexShrink: 0 }}>
            <button
              onClick={reset}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 50,
                border: "none",
                background: "#5b288f",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                fontWeight: 700,
                fontSize: "0.85rem",
                fontFamily: "inherit",
                cursor: "pointer",
                boxShadow: "0 8px 22px rgba(91,40,143,0.3)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-2px)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 12px 28px rgba(91,40,143,0.4)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(0)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 8px 22px rgba(91,40,143,0.3)";
              }}
            >
              Reset All <RotateCcw size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Section({
  title,
  children,
  noBorder,
}: {
  title: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div
      className="a11y-section"
      style={{
        padding: "18px 20px",
        borderBottom: noBorder ? "none" : "1px solid var(--border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 4,
            height: 13,
            borderRadius: 99,
            background: "#5b288f",
            display: "block",
            flexShrink: 0,
          }}
        />
        <p
          style={{
            fontSize: "0.68rem",
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--ink-60)",
            margin: 0,
            fontFamily: "poppins",
          }}
        >
          {title}
        </p>
      </div>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}
      >
        {children}
      </div>
    </div>
  );
}

function ToolButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`a11y-tool-btn${active ? " a11y-tool-btn--active" : ""}`}
    >
      {icon}
      {label}
    </button>
  );
}
